import { useCallback, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'
import {
  KnowledgeFacetsResponse,
  LIBRARY_FACET_RULE_FIELD_KINDS,
  LIBRARY_KPI_WINDOWS,
  LibraryFacetRuleField,
  LibraryKpiWindow,
} from '@digital-twins/shared-types'

import { knowledgeApi } from '@/lib/api'
import { SocialTopicFiltersUrl } from '@/pages/KnowledgeHubPage/social/useSocialTopicFilters'

/** The resources query's facet filters, each a list of facet value tokens (repeated params). */
export interface CollectionResourceFilters {
  market: Array<string>
  year: Array<string>
  brand: Array<string>
}

export type CollectionFacetFilterKey = keyof CollectionResourceFilters

/** The facet kind each filter reads its options from. */
export const COLLECTION_FACET_FILTER_KINDS: Record<CollectionFacetFilterKey, string> = {
  market: 'market',
  year: 'year',
  brand: 'brand',
}

const FILTER_KEYS: ReadonlyArray<CollectionFacetFilterKey> = ['market', 'year', 'brand']

/** The KPI window when the URL names none. */
export const DEFAULT_COLLECTION_KPI_WINDOW: LibraryKpiWindow = '30d'

/** Most tokens one facet filter sends (the backend's `ArrayMaxSize`). */
export const COLLECTION_FACET_FILTER_TOKENS_MAX = 50

/** URL params the collection filters own; every other param is left alone. */
const WINDOW_URL_KEY = 'window'

const FACET_TOKEN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export interface CollectionFiltersState {
  filters: CollectionResourceFilters
  window: LibraryKpiWindow
}

const EMPTY_FILTERS: CollectionResourceFilters = { market: [], year: [], brand: [] }

/** Facet tokens are UUIDs: anything else reads as unset. Deduped, capped. */
function parseTokens(values: Array<string>): Array<string> {
  const seen = new Set<string>()
  for (const value of values) {
    const token = value.trim().toLowerCase()
    if (FACET_TOKEN.test(token)) {
      seen.add(token)
    }
  }
  return [...seen].slice(0, COLLECTION_FACET_FILTER_TOKENS_MAX)
}

function isKpiWindow(value: string | null): value is LibraryKpiWindow {
  return value != null && (LIBRARY_KPI_WINDOWS as ReadonlyArray<string>).includes(value)
}

/** The state a collection URL carries; a value outside the contract reads as unset. */
export function parseCollectionSearchParams(params: URLSearchParams): CollectionFiltersState {
  const window = params.get(WINDOW_URL_KEY)
  return {
    filters: {
      market: parseTokens(params.getAll('market')),
      year: parseTokens(params.getAll('year')),
      brand: parseTokens(params.getAll('brand')),
    },
    window: isKpiWindow(window) ? window : DEFAULT_COLLECTION_KPI_WINDOW,
  }
}

/** `params` with the collection keys rewritten for `state` (the default window omitted). */
export function writeCollectionSearchParams(
  params: URLSearchParams,
  state: CollectionFiltersState,
): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const key of [...FILTER_KEYS, WINDOW_URL_KEY]) {
    next.delete(key)
  }
  for (const key of FILTER_KEYS) {
    for (const token of state.filters[key]) {
      next.append(key, token)
    }
  }
  if (state.window !== DEFAULT_COLLECTION_KPI_WINDOW) {
    next.append(WINDOW_URL_KEY, state.window)
  }
  return next
}

/** Only the collection keys of `params`, in a fixed order: what the parsed state depends on. */
function collectionSearchKey(params: URLSearchParams): string {
  const own = new URLSearchParams()
  for (const key of [...FILTER_KEYS, WINDOW_URL_KEY]) {
    for (const value of params.getAll(key)) {
      own.append(key, value)
    }
  }
  return own.toString()
}

function hasAnyFacetFilter(filters: CollectionResourceFilters): boolean {
  return FILTER_KEYS.some((key) => filters[key].length > 0)
}

export interface UseCollectionFiltersResult {
  filters: CollectionResourceFilters
  /** The KPI window, `30d` by default. */
  window: LibraryKpiWindow
  /** Any market, year or brand token is set (Studies and Audiences hide, D14). */
  hasFacetFilters: boolean
  /** Adds the option's tokens, or removes them when all are already set. */
  toggleOption: (key: CollectionFacetFilterKey, tokens: Array<string>) => void
  removeOption: (key: CollectionFacetFilterKey, tokens: Array<string>) => void
  setWindow: (window: LibraryKpiWindow) => void
  /** Clears the facet filters; the window stays. */
  clear: () => void
}

/**
 * The collection page's facet filters (`market`, `year`, `brand`, each a
 * list of facet value tokens) and its KPI window. With `url` they live in
 * the page URL, mirroring `useSocialTopicFilters`: repeated params, the
 * default window left out, writes replacing the history entry, unknown
 * values ignored. Without it the state is local.
 */
export function useCollectionFilters(url?: SocialTopicFiltersUrl): UseCollectionFiltersResult {
  const [localState, setLocalState] = useState<CollectionFiltersState>({
    filters: EMPTY_FILTERS,
    window: DEFAULT_COLLECTION_KPI_WINDOW,
  })

  const urlKey = url ? collectionSearchKey(url.searchParams) : null
  const urlState = useMemo(
    () => (urlKey == null ? null : parseCollectionSearchParams(new URLSearchParams(urlKey))),
    [urlKey],
  )
  const state = urlState ?? localState

  // The latest state ahead of the next render, so two changes in one tick both land.
  const latestRef = useRef(state)
  const seenStateRef = useRef(state)
  if (seenStateRef.current !== state) {
    seenStateRef.current = state
    latestRef.current = state
  }
  const urlRef = useRef(url)
  urlRef.current = url

  const apply = useCallback((next: CollectionFiltersState) => {
    latestRef.current = next
    const target = urlRef.current
    if (target) {
      target.setSearchParams((prev) => writeCollectionSearchParams(prev, next), { replace: true })
      return
    }
    setLocalState(next)
  }, [])

  const setTokens = useCallback(
    (key: CollectionFacetFilterKey, tokens: Array<string>) => {
      const current = latestRef.current
      apply({ ...current, filters: { ...current.filters, [key]: tokens } })
    },
    [apply],
  )

  const toggleOption = useCallback(
    (key: CollectionFacetFilterKey, tokens: Array<string>) => {
      const picked = parseTokens(tokens)
      if (picked.length === 0) {
        return
      }
      const current = latestRef.current.filters[key]
      const isAllSet = picked.every((token) => current.includes(token))
      if (isAllSet) {
        setTokens(key, current.filter((token) => !picked.includes(token)))
        return
      }
      setTokens(key, parseTokens([...current, ...picked]))
    },
    [setTokens],
  )

  const removeOption = useCallback(
    (key: CollectionFacetFilterKey, tokens: Array<string>) => {
      const picked = parseTokens(tokens)
      const current = latestRef.current.filters[key]
      if (!picked.some((token) => current.includes(token))) {
        return
      }
      setTokens(key, current.filter((token) => !picked.includes(token)))
    },
    [setTokens],
  )

  const setWindow = useCallback(
    (window: LibraryKpiWindow) => {
      const current = latestRef.current
      if (current.window === window || !isKpiWindow(window)) {
        return
      }
      apply({ ...current, window })
    },
    [apply],
  )

  const clear = useCallback(() => {
    const current = latestRef.current
    if (!hasAnyFacetFilter(current.filters)) {
      return
    }
    apply({ ...current, filters: EMPTY_FILTERS })
  }, [apply])

  const hasFacetFilters = hasAnyFacetFilter(state.filters)

  return useMemo(
    () => ({
      filters: state.filters,
      window: state.window,
      hasFacetFilters,
      toggleOption,
      removeOption,
      setWindow,
      clear,
    }),
    [state, hasFacetFilters, toggleOption, removeOption, setWindow, clear],
  )
}

/**
 * One facet option, grouped by value text as `GET /knowledge/facets` returns
 * it (AB6): picking it sends every token behind that text.
 */
export interface LibraryFacetOption {
  value: string
  tokens: Array<string>
  count: number
}

export interface CollectionFacetOptions {
  market: Array<LibraryFacetOption>
  year: Array<LibraryFacetOption>
  brand: Array<LibraryFacetOption>
}

export const LIBRARY_FACET_OPTIONS_SWR_KEY = 'library/facet-options'

/** Options per facet kind, values with no token dropped, by count then text. */
export function toLibraryFacetOptions(
  response: KnowledgeFacetsResponse | null | undefined,
): Map<string, Array<LibraryFacetOption>> {
  const byKind = new Map<string, Array<LibraryFacetOption>>()
  for (const facet of response?.facets ?? []) {
    const options = facet.values
      .map((value) => ({ value: value.value, tokens: value.tokens ?? [], count: value.count }))
      .filter((option) => option.tokens.length > 0)
      .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value))
    byKind.set(facet.kind, options)
  }
  return byKind
}

export interface UseLibraryFacetOptionsResult {
  /** Options for a rule field's facet kind (`brands` reads `brand`), sorted by count. */
  optionsFor: (field: LibraryFacetRuleField) => Array<LibraryFacetOption>
  /** The three filter bar facets. */
  filterOptions: CollectionFacetOptions
  isLoading: boolean
  hasError: boolean
}

/**
 * The facet options over `knowledgeApi.listFacets` (`GET /knowledge/facets`),
 * reading AB6's `tokens` (`tokens ?? []`, a value with no token is dropped).
 * Feeds the filter bar (AF4) and the create dialog's anchor pickers (AF6).
 */
export function useLibraryFacetOptions(enabled = true): UseLibraryFacetOptionsResult {
  const { data, error, isLoading } = useSWR<KnowledgeFacetsResponse>(
    enabled ? LIBRARY_FACET_OPTIONS_SWR_KEY : null,
    () => knowledgeApi.listFacets(),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  )

  const byKind = useMemo(() => toLibraryFacetOptions(data), [data])

  const optionsFor = useCallback(
    (field: LibraryFacetRuleField) => byKind.get(LIBRARY_FACET_RULE_FIELD_KINDS[field]) ?? [],
    [byKind],
  )

  const filterOptions = useMemo<CollectionFacetOptions>(
    () => ({
      market: byKind.get(COLLECTION_FACET_FILTER_KINDS.market) ?? [],
      year: byKind.get(COLLECTION_FACET_FILTER_KINDS.year) ?? [],
      brand: byKind.get(COLLECTION_FACET_FILTER_KINDS.brand) ?? [],
    }),
    [byKind],
  )

  return {
    optionsFor,
    filterOptions,
    isLoading: enabled && isLoading,
    hasError: Boolean(error),
  }
}
